import * as ngCdkOverlay from '@angular/cdk/overlay'
import * as ngCore from '@angular/core'

/**
 * Attached overlay component that displays a component attached to a specific element.
 * The overlay is positioned relative to the element and blocks any interaction with the page.
 *
 * #### Inputs
 * - {@link Component.originSg|origin}!
 * - {@link Component.offsetXSg|offsetX}?
 * - {@link Component.offsetYSg|offsetY}?
 * - {@link Component.hasBackdropSg|hasBackdrop}?
 * - {@link Component.backdropClassSg|backdropClass}?
 * - {@link Component.backdropClick|backdropClick}?
 *
 * #### Outputs
 * - {@link Component.backdropClick|backdropClick}
 *
 * @example
 * ```html
 * <!--
 *  The '#btnOrigin' template variable is used as the origin for the overlay.
 *  The overlay will be displayed when isOpen is set to true.
 *  Clicking on the button sets isOpen to 'true', displaying the overlay.
 * -->
 * <button #btnOrigin (click)="isOpen = true">Open Overlay</button>
 *
 * <!--
 *  Declaration of the overlay, which uses content projection to specify the content to display inside the overlay.
 * -->
 * <atn-ds-attached-overlay [origin]="btnOrigin" *ngIf="isOpen">
 *  <div>Overlay Content</div>
 * </atn-ds-attached-overlay>
 * ```
 */
@ngCore.Component({
  imports: [ngCdkOverlay.OverlayModule],
  selector: 'atn-ds-attached-overlay',
  template: `
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="originSg()"
      [cdkConnectedOverlayOpen]="true"
      [cdkConnectedOverlayHasBackdrop]="hasBackdropSg()"
      [cdkConnectedOverlayOffsetX]="offsetXSg()"
      [cdkConnectedOverlayOffsetY]="offsetYSg()"
      [cdkConnectedOverlayPositions]="connectedPositionsSg()"
      (overlayOutsideClick)="
        $event.stopPropagation(); backdropClick.emit($event)
      "
    >
      <ng-content></ng-content>
    </ng-template>
  `,
})
export class Component {
  /** @see {@link ngCdkOverlay.CdkConnectedOverlay} */
  private readonly _connectedOverlaySg =
    ngCore.viewChild<ngCdkOverlay.CdkConnectedOverlay>(
      ngCdkOverlay.CdkConnectedOverlay,
    )
  /**
   * The element or position that the overlay is connected to.
   */
  public originSg = ngCore.input.required<
    | ngCdkOverlay.CdkOverlayOrigin
    | ngCdkOverlay.FlexibleConnectedPositionStrategyOrigin
  >({ alias: 'origin' })
  /**
   * The offset along the x-axis of the overlay connection point.
   *
   * @default 0
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public offsetXSg = ngCore.input(0, {
    alias: 'offsetX',
    transform: (value: number | undefined = 0) => value,
  })
  /**
   * The offset along the y-axis of the overlay connection point.
   *
   * @default 0
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public offsetYSg = ngCore.input(0, {
    alias: 'offsetY',
    transform: (value: number | undefined = 0) => value,
  })

  /** @see {@link ngCdkOverlay.ConnectedPosition}  */
  public connectedPositionsSg = ngCore.input<ngCdkOverlay.ConnectedPosition[]>(
    [],
    { alias: 'connectedPositions' },
  )

  /**
   * Whether the dialog has a backdrop.
   *
   * @default false
   */
  public hasBackdropSg = ngCore.input(false, { alias: 'hasBackdrop' })

  /**
   * The class to apply to the backdrop.
   *
   * @default ''
   */
  public backdropClassSg = ngCore.input('', { alias: 'backdropClass' })

  /**
   * Event emitted when the backdrop is clicked.
   */
  public backdropClick = ngCore.output<MouseEvent>()

  constructor() {
    // When offset inputs change while overlay is open, we need to manually
    // trigger position recalculation. This is because CDK overlay doesn't
    // automatically react to offset changes after initial render.
    ngCore.effect(() => {
      const overlay = this._connectedOverlaySg()
      if (!overlay) {
        return undefined
      }

      // Track offset changes to trigger position recalculation.
      // Variables must be accessed here so effect reruns when they change.
      const offsetX = this.offsetXSg()
      const offsetY = this.offsetYSg()
      // NOTE: Safety check for overlayRef. Despite TypeScript types
      // suggesting it's always defined, there's a race condition during
      // initialization where it can be temporarily undefined
      if (overlay.overlayRef) {
        overlay.overlayRef.updatePosition()
      }
    })
  }
}
